import React from 'react';
import { PageWrapper } from '@components/layout/page-wrapper';
import { GlassCard } from '@components/primitives/glass-card';
import styled from 'styled-components';
import { spacing } from '@config/spacing';
import { typography } from '@config/typography';
import { colors } from '@config/seafoam_colors';

const ToSContainer = styled.div`
  padding: ${spacing['4xl']} ${spacing.xl};
  max-width: 900px;
  margin: 0 auto;
`;

const ToSCard = styled(GlassCard)`
  background: ${colors.white};
  padding: ${spacing['4xl']};
  gap: ${spacing.xl};

  @media (max-width: 768px) {
    padding: ${spacing['2xl']};
  }
`;

const Title = styled.h1`
  font-family: ${typography.fontFamily.heading};
  font-size: ${typography.fontSize['4xl']};
  font-weight: ${typography.fontWeights.semibold};
  color: ${colors.text};
  margin: 0 0 ${spacing.md} 0;
  text-align: center;
`;

const LastUpdated = styled.p`
  text-align: center;
  color: ${colors.textSecondary};
  font-size: ${typography.fontSize.sm};
  margin: 0 0 ${spacing['2xl']} 0;
`;

const Section = styled.section`
  margin-bottom: ${spacing['2xl']};
`;

const SectionTitle = styled.h2`
  font-family: ${typography.fontFamily.heading};
  font-size: ${typography.fontSize['2xl']};
  font-weight: ${typography.fontWeights.semibold};
  color: ${colors.text};
  margin: ${spacing.xl} 0 ${spacing.md} 0;
`;

const SubsectionTitle = styled.h3`
  font-family: ${typography.fontFamily.heading};
  font-size: ${typography.fontSize.xl};
  font-weight: ${typography.fontWeights.semibold};
  color: ${colors.text};
  margin: ${spacing.lg} 0 ${spacing.sm} 0;
`;

const Paragraph = styled.p`
  font-family: ${typography.fontFamily.body};
  font-size: ${typography.fontSize.base};
  color: ${colors.text};
  line-height: ${typography.lineHeight.bodyRelaxed};
  margin: ${spacing.md} 0;
`;

const List = styled.ul`
  margin: ${spacing.md} 0;
  padding-left: ${spacing.xl};
  
  li {
    font-family: ${typography.fontFamily.body};
    font-size: ${typography.fontSize.base};
    color: ${colors.text};
    line-height: ${typography.lineHeight.bodyRelaxed};
    margin: ${spacing.sm} 0;
  }
`;

const ContactInfo = styled.div`
  background: rgba(255, 255, 255, 0.1);
  border-radius: ${spacing.md};
  padding: ${spacing.lg};
  margin: ${spacing.lg} 0;
`;

const Link = styled.a`
  color: ${colors.primary};
  text-decoration: underline;

  &:hover {
    color: ${colors.primaryDark};
  }
`;

const ImportantNotice = styled.div`
  background: rgba(255, 200, 0, 0.1);
  border-left: 4px solid ${colors.warning};
  padding: ${spacing.md};
  margin: ${spacing.lg} 0;
  border-radius: ${spacing.sm};
  font-weight: ${typography.fontWeights.semibold};
`;

/**
 * Terms of Service Page
 * Displays the complete terms of service for HomeCEO
 */
export const TermsOfServicePage: React.FC = () => {
  return (
    <PageWrapper contained>
      <ToSContainer>
        <ToSCard>
          <Title>TERMS OF SERVICE</Title>
          <LastUpdated>Last updated October 14, 2026</LastUpdated>

          <Section>
            <SectionTitle>AGREEMENT TO OUR LEGAL TERMS</SectionTitle>
            <Paragraph>
              We are <strong>Lat Long Labs LLC</strong> (doing business as <strong>The Home CEO</strong>) (&quot;Company,&quot;
              &quot;we,&quot; &quot;us,&quot; &quot;our&quot;), a company registered in California, United States at 3159 Stelling Dr,
              Palo Alto, CA 94303.
            </Paragraph>
            <Paragraph>
              We operate the website <Link href="https://homeceo.ai">https://homeceo.ai</Link> (the &quot;Site&quot;),
              the mobile application The Home CEO (the &quot;App&quot;), as well as any other related products and services
              that refer or link to these legal terms (the &quot;Legal Terms&quot;) (collectively, the &quot;Services&quot;).
            </Paragraph>
            <Paragraph>
              You can contact us by phone at <Link href="tel:4159069228">415-906-9228</Link>, email at{' '}
              <Link href="mailto:support@homeceo.ai">support@homeceo.ai</Link>, or by mail to 3159 Stelling Dr,
              Palo Alto, CA 94303, United States.
            </Paragraph>
            
            <ImportantNotice>
              These Legal Terms constitute a legally binding agreement made between you, whether personally or on
              behalf of an entity (&quot;you&quot;), and Lat Long Labs LLC, concerning your access to and use of the Services. 
              You agree that by accessing the Services, you have read, understood, and agreed to be bound by all of 
              these Legal Terms. IF YOU DO NOT AGREE WITH ALL OF THESE LEGAL TERMS, THEN YOU ARE EXPRESSLY 
              PROHIBITED FROM USING THE SERVICES AND YOU MUST DISCONTINUE USE IMMEDIATELY.
            </ImportantNotice>

            <Paragraph>
              Supplemental terms and conditions or documents that may be posted on the Services from time to time
              are hereby expressly incorporated herein by reference. We reserve the right, in our sole discretion,
              to make changes or modifications to these Legal Terms from time to time. We will alert you about any
              changes by updating the &quot;Last updated&quot; date of these Legal Terms.
            </Paragraph>

            <Paragraph>
              <strong>The Services are intended for users who are at least 18 years old.</strong> Persons under the 
              age of 18 are not permitted to use or register for the Services.
            </Paragraph>
          </Section>

          <Section>
            <SectionTitle>TABLE OF CONTENTS</SectionTitle>
            <List>
              <li>1. OUR SERVICES</li>
              <li>2. INTELLECTUAL PROPERTY RIGHTS</li>
              <li>3. USER REPRESENTATIONS</li>
              <li>4. USER REGISTRATION</li>
              <li>5. PRODUCTS</li>
              <li>6. PURCHASES AND PAYMENT</li>
              <li>7. SUBSCRIPTIONS</li>
              <li>8. RETURN/REFUNDS POLICY</li>
              <li>9. SOFTWARE</li>
              <li>10. PROHIBITED ACTIVITIES</li>
              <li>11. USER GENERATED CONTRIBUTIONS</li>
              <li>12. CONTRIBUTION LICENSE</li>
              <li>13. GUIDELINES FOR REVIEWS</li>
              <li>14. MOBILE APPLICATION LICENSE</li>
              <li>15. SOCIAL MEDIA</li>
              <li>16. ADVERTISERS</li>
              <li>17. SERVICES MANAGEMENT</li>
              <li>18. PRIVACY POLICY</li>
              <li>19. COPYRIGHT INFRINGEMENTS</li>
              <li>20. TERM AND TERMINATION</li>
              <li>21. MODIFICATIONS AND INTERRUPTIONS</li>
              <li>22. GOVERNING LAW</li>
              <li>23. DISPUTE RESOLUTION</li>
              <li>24. CORRECTIONS</li>
              <li>25. DISCLAIMER</li>
              <li>26. LIMITATIONS OF LIABILITY</li>
              <li>27. INDEMNIFICATION</li>
              <li>28. USER DATA</li>
              <li>29. ELECTRONIC COMMUNICATIONS, TRANSACTIONS, AND SIGNATURES</li>
              <li>30. SMS TERMS</li>
              <li>31. CALIFORNIA USERS AND RESIDENTS</li>
              <li>32. MISCELLANEOUS</li>
              <li>33. CONTACT US</li>
            </List>
          </Section>

          <Section>
            <SectionTitle>1. OUR SERVICES</SectionTitle>
            <Paragraph>
              The information provided when using the Services is not intended for distribution to or use by any 
              person or entity in any jurisdiction or country where such distribution or use would be contrary to 
              law or regulation.
            </Paragraph>
            <Paragraph>
              The Services are not tailored to comply with industry-specific regulations (Health Insurance Portability 
              and Accountability Act (HIPAA), Federal Information Security Management Act (FISMA), etc.), so if your 
              interactions would be subjected to such laws, you may not use the Services.
            </Paragraph>
          </Section>

          <Section>
            <SectionTitle>2. INTELLECTUAL PROPERTY RIGHTS</SectionTitle>
            
            <SubsectionTitle>Our intellectual property</SubsectionTitle>
            <Paragraph>
              We are the owner or the licensee of all intellectual property rights in our Services, including all
              source code, databases, functionality, software, website designs, audio, video, text, photographs, and
              graphics in the Services (collectively, the &quot;Content&quot;), as well as the trademarks, service marks, and
              logos contained therein (the &quot;Marks&quot;).
            </Paragraph>
            <Paragraph>
              Our Content and Marks are protected by copyright and trademark laws (and various other intellectual 
              property rights and unfair competition laws) and treaties in the United States and around the world.
            </Paragraph>

            <SubsectionTitle>Your use of our Services</SubsectionTitle>
            <Paragraph>
              Subject to your compliance with these Legal Terms, we grant you a non-exclusive, non-transferable, 
              revocable license to:
            </Paragraph>
            <List>
              <li>access the Services; and</li>
              <li>download or print a copy of any portion of the Content to which you have properly gained access,</li>
            </List>
            <Paragraph>
              solely for your personal, non-commercial use.
            </Paragraph>

            <SubsectionTitle>Your submissions and contributions</SubsectionTitle>
            <Paragraph>
              <strong>Submissions:</strong> By directly sending us any question, comment, suggestion, idea, feedback,
              or other information about the Services (&quot;Submissions&quot;), you agree to assign to us all intellectual
              property rights in such Submission.
            </Paragraph>
            <Paragraph>
              <strong>Contributions:</strong> The Services may invite you to chat, contribute to, or participate in
              blogs, message boards, online forums, and other functionality during which you may create, submit, post,
              display, transmit, publish, distribute, or broadcast content and materials to us or through the Services
              (&quot;Contributions&quot;).
            </Paragraph>
            <Paragraph>
              When you post Contributions, you grant us an unrestricted, unlimited, irrevocable, perpetual, 
              non-exclusive, transferable, royalty-free, fully-paid, worldwide right, and license to use, copy, 
              reproduce, distribute, sell, resell, publish, broadcast, retitle, store, publicly perform, publicly 
              display, reformat, translate, excerpt, and exploit your Contributions for any purpose, commercial, 
              advertising, or otherwise.
            </Paragraph>

            <SubsectionTitle>Copyright infringement</SubsectionTitle>
            <Paragraph>
              We respect the intellectual property rights of others. If you believe that any material available on
              or through the Services infringes upon any copyright you own or control, please immediately refer to
              the &quot;COPYRIGHT INFRINGEMENTS&quot; section below.
            </Paragraph>
          </Section>

          <Section>
            <SectionTitle>3. USER REPRESENTATIONS</SectionTitle>
            <Paragraph>
              By using the Services, you represent and warrant that:
            </Paragraph>
            <List>
              <li>All registration information you submit will be true, accurate, current, and complete</li>
              <li>You will maintain the accuracy of such information and promptly update such registration information as necessary</li>
              <li>You have the legal capacity and you agree to comply with these Legal Terms</li>
              <li>You are not a minor in the jurisdiction in which you reside</li>
              <li>You will not access the Services through automated or non-human means</li>
              <li>You will not use the Services for any illegal or unauthorized purpose</li>
              <li>Your use of the Services will not violate any applicable law or regulation</li>
            </List>
          </Section>

          <Section>
            <SectionTitle>4. USER REGISTRATION</SectionTitle>
            <Paragraph>
              You may be required to register to use the Services. You agree to keep your password confidential and 
              will be responsible for all use of your account and password. We reserve the right to remove, reclaim, 
              or change a username you select if we determine, in our sole discretion, that such username is 
              inappropriate, obscene, or otherwise objectionable.
            </Paragraph>
          </Section>

          <Section>
            <SectionTitle>6. PURCHASES AND PAYMENT</SectionTitle>
            <Paragraph>
              We accept the following forms of payment:
            </Paragraph>
            <List>
              <li>Visa</li>
              <li>Mastercard</li>
              <li>American Express</li>
              <li>PayPal</li>
            </List>
            <Paragraph>
              You agree to provide current, complete, and accurate purchase and account information for all purchases 
              made via the Services. Sales tax will be added to the price of purchases as deemed required by us. All 
              payments shall be in US dollars.
            </Paragraph>
          </Section>

          <Section>
            <SectionTitle>7. SUBSCRIPTIONS</SectionTitle>
            
            <SubsectionTitle>Billing and Renewal</SubsectionTitle>
            <Paragraph>
              Your subscription will continue and automatically renew unless canceled. You consent to our charging 
              your payment method on a recurring basis without requiring your prior approval for each recurring charge. 
              The length of your billing cycle is monthly.
            </Paragraph>

            <SubsectionTitle>Free Trial</SubsectionTitle>
            <Paragraph>
              We offer a <strong>7-day free trial</strong> to new users who register with the Services. The account
              will be charged according to the user&apos;s chosen subscription at the end of the free trial.
            </Paragraph>

            <SubsectionTitle>Cancellation</SubsectionTitle>
            <Paragraph>
              You can cancel your subscription at any time by logging into your account. Your cancellation will take 
              effect at the end of the current paid term. If you have any questions or are unsatisfied with our 
              Services, please email us at <Link href="mailto:support@homeceo.ai">support@homeceo.ai</Link>.
            </Paragraph>
          </Section>

          <Section>
            <SectionTitle>8. RETURN/REFUNDS POLICY</SectionTitle>
            <Paragraph>
              <strong>All sales are final and no refund will be issued.</strong>
            </Paragraph>
          </Section>

          <Section>
            <SectionTitle>10. PROHIBITED ACTIVITIES</SectionTitle>
            <Paragraph>
              You may not access or use the Services for any purpose other than that for which we make the Services 
              available. As a user of the Services, you agree not to:
            </Paragraph>
            <List>
              <li>Systematically retrieve data or other content from the Services to create or compile a collection, compilation, database, or directory without written permission from us</li>
              <li>Trick, defraud, or mislead us and other users, especially in any attempt to learn sensitive account information such as user passwords</li>
              <li>Circumvent, disable, or otherwise interfere with security-related features of the Services</li>
              <li>Disparage, tarnish, or otherwise harm, in our opinion, us and/or the Services</li>
              <li>Use any information obtained from the Services in order to harass, abuse, or harm another person</li>
              <li>Use the Services in a manner inconsistent with any applicable laws or regulations</li>
              <li>Engage in unauthorized framing of or linking to the Services</li>
              <li>Upload or transmit viruses, Trojan horses, or other malicious material</li>
              <li>Engage in any automated use of the system, such as using scripts to send comments or messages</li>
              <li>Interfere with, disrupt, or create an undue burden on the Services</li>
              <li>Attempt to bypass any measures of the Services designed to prevent or restrict access</li>
              <li>Copy or adapt the Services&apos; software</li>
              <li>Use the Services as part of any effort to compete with us</li>
            </List>
          </Section>

          <Section>
            <SectionTitle>14. MOBILE APPLICATION LICENSE</SectionTitle>
            <Paragraph>
              If you access the Services via the App, we grant you a revocable, non-exclusive, non-transferable, 
              limited right to install and use the App on wireless electronic devices owned or controlled by you, 
              and to access and use the App on such devices strictly in accordance with these Legal Terms.
            </Paragraph>
          </Section>

          <Section>
            <SectionTitle>18. PRIVACY POLICY</SectionTitle>
            <Paragraph>
              We care about data privacy and security. Please review our Privacy Policy:{' '}
              <Link href="/legal/privacy-policy">https://homeceo.ai/legal/privacy-policy</Link>. By using 
              the Services, you agree to be bound by our Privacy Policy, which is incorporated into these Legal Terms.
            </Paragraph>
            <Paragraph>
              Please be advised the Services are hosted in the United States. If you access the Services from any 
              other region of the world with laws or other requirements governing personal data collection, use, or 
              disclosure that differ from applicable laws in the United States, then through your continued use of 
              the Services, you are transferring your data to the United States.
            </Paragraph>
          </Section>

          <Section>
            <SectionTitle>19. COPYRIGHT INFRINGEMENTS</SectionTitle>
            <Paragraph>
              We respect the intellectual property rights of others. If you believe that any material available on or
              through the Services infringes upon any copyright you own or control, please immediately notify us using
              the contact information provided below (a &quot;Notification&quot;). Please be advised that pursuant to applicable
              law you may be held liable for damages if you make material misrepresentations in a Notification.
            </Paragraph>
            <Paragraph>
              For DMCA takedown notifications, please contact us via{' '}
              <Link href="/legal/contact-us">/legal/contact-us</Link>.
            </Paragraph>
          </Section>

          <Section>
            <SectionTitle>20. TERM AND TERMINATION</SectionTitle>
            <Paragraph>
              These Legal Terms shall remain in full force and effect while you use the Services. We reserve the right 
              to, in our sole discretion and without notice or liability, deny access to and use of the Services 
              (including blocking certain IP addresses), to any person for any reason or for no reason, including 
              without limitation for breach of any representation, warranty, or covenant contained in these Legal Terms 
              or of any applicable law or regulation.
            </Paragraph>
            <Paragraph>
              If we terminate or suspend your account for any reason, you are prohibited from registering and creating 
              a new account under your name, a fake or borrowed name, or the name of any third party.
            </Paragraph>
          </Section>

          <Section>
            <SectionTitle>22. GOVERNING LAW</SectionTitle>
            <Paragraph>
              These Legal Terms and your use of the Services are governed by and construed in accordance with the laws 
              of the <strong>State of Delaware</strong> applicable to agreements made and to be entirely performed 
              within the State of Delaware, without regard to its conflict of law principles.
            </Paragraph>
          </Section>

          <Section>
            <SectionTitle>23. DISPUTE RESOLUTION</SectionTitle>
            
            <SubsectionTitle>Binding Arbitration</SubsectionTitle>
            <Paragraph>
              If the Parties are unable to resolve a Dispute through informal negotiations, the Dispute will be finally 
              and exclusively resolved by binding arbitration. <strong>YOU UNDERSTAND THAT WITHOUT THIS PROVISION, YOU 
              WOULD HAVE THE RIGHT TO SUE IN COURT AND HAVE A JURY TRIAL.</strong>
            </Paragraph>
            <Paragraph>
              The arbitration shall be commenced and conducted under the Commercial Arbitration Rules of the American
              Arbitration Association (&quot;AAA&quot;). Except as otherwise provided herein, the arbitration will take place in{' '}
              <strong>Santa Clara, California</strong>.
            </Paragraph>
            <Paragraph>
              In no event shall any Dispute brought by either Party related in any way to the Services be commenced 
              more than <strong>one (1) year</strong> after the cause of action arose.
            </Paragraph>

            <SubsectionTitle>Restrictions</SubsectionTitle>
            <Paragraph>
              The Parties agree that any arbitration shall be limited to the Dispute between the Parties individually. 
              To the full extent permitted by law:
            </Paragraph>
            <List>
              <li>No arbitration shall be joined with any other proceeding</li>
              <li>There is no right or authority for any Dispute to be arbitrated on a class-action basis or to utilize class action procedures</li>
              <li>There is no right or authority for any Dispute to be brought in a purported representative capacity on behalf of the general public</li>
            </List>
          </Section>

          <Section>
            <SectionTitle>25. DISCLAIMER</SectionTitle>
            <ImportantNotice>
              THE SERVICES ARE PROVIDED ON AN AS-IS AND AS-AVAILABLE BASIS. YOU AGREE THAT YOUR USE OF THE SERVICES 
              WILL BE AT YOUR SOLE RISK. TO THE FULLEST EXTENT PERMITTED BY LAW, WE DISCLAIM ALL WARRANTIES, EXPRESS 
              OR IMPLIED, IN CONNECTION WITH THE SERVICES AND YOUR USE THEREOF.
            </ImportantNotice>
          </Section>

          <Section>
            <SectionTitle>26. LIMITATIONS OF LIABILITY</SectionTitle>
            <Paragraph>
              IN NO EVENT WILL WE OR OUR DIRECTORS, EMPLOYEES, OR AGENTS BE LIABLE TO YOU OR ANY THIRD PARTY FOR ANY 
              DIRECT, INDIRECT, CONSEQUENTIAL, EXEMPLARY, INCIDENTAL, SPECIAL, OR PUNITIVE DAMAGES, INCLUDING LOST 
              PROFIT, LOST REVENUE, LOSS OF DATA, OR OTHER DAMAGES ARISING FROM YOUR USE OF THE SERVICES.
            </Paragraph>
            <Paragraph>
              NOTWITHSTANDING ANYTHING TO THE CONTRARY CONTAINED HEREIN, OUR LIABILITY TO YOU FOR ANY CAUSE WHATSOEVER 
              AND REGARDLESS OF THE FORM OF THE ACTION, WILL AT ALL TIMES BE LIMITED TO <strong>THE AMOUNT PAID, IF ANY, 
              BY YOU TO US DURING THE SIX (6) MONTH PERIOD PRIOR TO ANY CAUSE OF ACTION ARISING</strong>.
            </Paragraph>
          </Section>

          <Section>
            <SectionTitle>27. INDEMNIFICATION</SectionTitle>
            <Paragraph>
              You agree to defend, indemnify, and hold us harmless, including our subsidiaries, affiliates, and all of
              our respective officers, agents, partners, and employees, from and against any loss, damage, liability,
              claim, or demand, including reasonable attorneys&apos; fees and expenses, made by any third party due to or
              arising out of:
            </Paragraph>
            <List>
              <li>Your Contributions</li>
              <li>Use of the Services</li>
              <li>Breach of these Legal Terms</li>
              <li>Any breach of your representations and warranties set forth in these Legal Terms</li>
              <li>Your violation of the rights of a third party, including but not limited to intellectual property rights</li>
              <li>Any overt harmful act toward any other user of the Services</li>
            </List>
          </Section>

          <Section>
            <SectionTitle>28. USER DATA</SectionTitle>
            <Paragraph>
              We will maintain certain data that you transmit to the Services for the purpose of managing the 
              performance of the Services. Although we perform regular routine backups of data, you are solely 
              responsible for all data that you transmit or that relates to any activity you have undertaken using 
              the Services.
            </Paragraph>
          </Section>

          <Section>
            <SectionTitle>29. ELECTRONIC COMMUNICATIONS, TRANSACTIONS, AND SIGNATURES</SectionTitle>
            <Paragraph>
              Visiting the Services, sending us emails, and completing online forms constitute electronic communications. 
              You consent to receive electronic communications, and you agree that all agreements, notices, disclosures, 
              and other communications we provide to you electronically, via email and on the Services, satisfy any 
              legal requirement that such communication be in writing.
            </Paragraph>
          </Section>

          <Section id="sms-terms">
            <SectionTitle>30. SMS TERMS</SectionTitle>
            <Paragraph>
              By opting in to receive text messages from <strong>The Home CEO</strong>, you agree to receive SMS and
              MMS messages about your account and the Services at the mobile number you provide.
            </Paragraph>

            <SubsectionTitle>Message and Data Rates</SubsectionTitle>
            <Paragraph>
              Message and data rates may apply to any SMS or MMS messages sent or received. The rates are determined
              by your carrier and the specifics of your mobile plan.
            </Paragraph>

            <SubsectionTitle>Opting Out</SubsectionTitle>
            <Paragraph>
              If at any time you wish to stop receiving text messages from us, reply &quot;STOP&quot; to any message we send.
            </Paragraph>

            <SubsectionTitle>Support</SubsectionTitle>
            <Paragraph>
              If you have any questions or need assistance regarding our text messages, please email us at{' '}
              <Link href="mailto:support@homeceo.ai">support@homeceo.ai</Link> or call us at{' '}
              <Link href="tel:4159069228">415-906-9228</Link>.
            </Paragraph>
          </Section>

          <Section>
            <SectionTitle>31. CALIFORNIA USERS AND RESIDENTS</SectionTitle>
            <Paragraph>
              If any complaint with us is not satisfactorily resolved, you can contact the Complaint Assistance Unit 
              of the Division of Consumer Services of the California Department of Consumer Affairs in writing at 
              1625 North Market Blvd., Suite N 112, Sacramento, California 95834 or by telephone at (800) 952-5210 or 
              (916) 445-1254.
            </Paragraph>
          </Section>

          <Section>
            <SectionTitle>33. CONTACT US</SectionTitle>
            <Paragraph>
              In order to resolve a complaint regarding the Services or to receive further information regarding use 
              of the Services, please contact us at:
            </Paragraph>
            <ContactInfo>
              <strong>Lat Long Labs LLC</strong><br />
              3159 Stelling Dr<br />
              Palo Alto, CA 94303<br />
              United States<br />
              Phone: <Link href="tel:4159069228">415-906-9228</Link><br />
              Email: <Link href="mailto:support@homeceo.ai">support@homeceo.ai</Link>
            </ContactInfo>
          </Section>
        </ToSCard>
      </ToSContainer>
    </PageWrapper>
  );
};

